import React, { useCallback } from 'react';
import { useContext } from 'use-context-selector';
import { GameState } from 'contexts/gameState';
import './options.scss';

const Options: React.FC<{}> = () => {
  const {
    simulationCount,
    maxWave,
    smartMissilesUnlocked,
    deathWaveUnlocked,
    chainLightningUnlocked,
    chronoFieldUnlocked,
    goldenTowerUnlocked,
    innerLandMinesUnlocked,
    poisonSwampUnlocked,
    blackHoleUnlocked,
    spotlightUnlocked,

    standardResearch,
    tradeoffResearch,
    waveReductionResearch,
    perkOptionResearch,

    coinBonuses,
    coinsPerKill,
    coinsPerWave,
    goldenTowerBonus,
    goldenTowerDuration,
    goldenTowerCooldown,
    blackHoleCoins,
    blackHoleCooldown,
    blackHoleCoverage,
    blackHoleDuration,
    spotlightAngle,
    spotlightCoins,
    spotlightQuantity,
    criticalCoin,
    waveSkip,
    introSprint,

    waveAccelerator,
    autopick,
    gameSpeed,

    setSimulationCount,
    setMaxWave,

    setSmartMissilesUnlocked,
    setDeathWaveUnlocked,
    setChainLightningUnlocked,
    setChronoFieldUnlocked,
    setGoldenTowerUnlocked,
    setInnerLandMinesUnlocked,
    setPoisonSwampUnlocked,
    setBlackHoleUnlocked,
    setSpotlightUnlocked,

    setStandardResearch,
    setTradeoffResearch,
    setWaveReductionResearch,
    setPerkOptionResearch,

    setCoinBonuses,
    setCoinsPerKill,
    setCoinsPerWave,
    setGoldenTowerBonus,
    setGoldenTowerDuration,
    setGoldenTowerCooldown,
    setBlackHoleCoins,
    setBlackHoleCooldown,
    setBlackHoleCoverage,
    setBlackHoleDuration,
    setSpotlightAngle,
    setSpotlightCoins,
    setSpotlightQuantity,
    setCriticalCoin,
    setWaveSkip,
    setIntroSprint,

    setWaveAccelerator,
    setAutopick,

    setGameSpeed,
  } = useContext(GameState);

  const resetAll = useCallback(() => {
    localStorage.clear();
    window.location.reload();
  }, []);

  return (
    <div id='options-pane'>
      <div id='stats' className='option-menu'>
        <div className='title'>Stats</div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='25'
            value={standardResearch}
            onChange={setStandardResearch}
          />
          Standard Perk Bonus Lab
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='10'
            value={tradeoffResearch}
            onChange={setTradeoffResearch}
          />
          Tradeoff Bonus Lab
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='100'
            value={waveReductionResearch}
            onChange={setWaveReductionResearch}
          />
          Wave Reduction Lab
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='4'
            value={perkOptionResearch}
            onChange={setPerkOptionResearch}
          />
          Perk Options
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            step='.01'
            value={coinBonuses}
            onChange={setCoinBonuses}
          />
          Coin Bonuses on T1
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            step='.01'
            value={coinsPerKill}
            onChange={setCoinsPerKill}
          />
          Coins Per Kill
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            value={coinsPerWave}
            onChange={setCoinsPerWave}
          />
          Coins Per Wave
        </div>
        {goldenTowerUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='0'
              step='.1'
              value={goldenTowerBonus}
              onChange={setGoldenTowerBonus}
            />
            Golden Tower Bonus
          </div>
        )}
        {goldenTowerUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='0'
              max='65'
              step='1'
              value={goldenTowerDuration}
              onChange={setGoldenTowerDuration}
            />
            Golden Tower Duration (s)
          </div>
        )}
        {goldenTowerUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='150'
              max='300'
              step='10'
              value={goldenTowerCooldown}
              onChange={setGoldenTowerCooldown}
            />
            Golden Tower Cooldown (s)
          </div>
        )}
        {blackHoleUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='0'
              max='100'
              step='.1'
              value={blackHoleCoverage}
              onChange={setBlackHoleCoverage}
            />
            Percent of enemies that die in Black Hole while active
          </div>
        )}
        {blackHoleUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='1'
              max='13'
              step='.1'
              value={blackHoleCoins}
              onChange={setBlackHoleCoins}
            />
            Black Hole Coin Multiplier (1 if not researched)
          </div>
        )}
        {blackHoleUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='0'
              max='30'
              step='1'
              value={blackHoleDuration}
              onChange={setBlackHoleDuration}
            />
            Black Hole Duration (s)
          </div>
        )}
        {blackHoleUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='50'
              max='200'
              step='10'
              value={blackHoleCooldown}
              onChange={setBlackHoleCooldown}
            />
            Black Hole Cooldown (s)
          </div>
        )}
        {spotlightUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='1'
              max='3'
              step='.1'
              value={spotlightCoins}
              onChange={setSpotlightCoins}
            />
            Spotlight Coins (x)
          </div>
        )}
        {spotlightUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='1'
              max='3'
              step='1'
              value={spotlightQuantity}
              onChange={setSpotlightQuantity}
            />
            Spotlight Quantity
          </div>
        )}
        {spotlightUnlocked && (
          <div className='option'>
            <input
              type='number'
              min='30'
              max='60'
              step='1'
              value={spotlightAngle}
              onChange={setSpotlightAngle}
            />
            Spotlight Angle
          </div>
        )}
        <div className='option'>
          <input
            type='number'
            min='0'
            max='33'
            step='1'
            value={criticalCoin}
            onChange={setCriticalCoin}
          />
          Critical Coin % (0 if unequipped)
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='19'
            step='1'
            value={waveSkip}
            onChange={setWaveSkip}
          />
          Wave Skip % (0 if unequipped)
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='50'
            step='1'
            value={introSprint}
            onChange={setIntroSprint}
          />
          Intro Sprint (waves, 0 if unequipped)
        </div>
        <div className='option'>
          <input
            type='number'
            min='0'
            max='54'
            step='1'
            value={waveAccelerator}
            onChange={setWaveAccelerator}
          />
          Wave Accelerator (%, 0 if unequipped)
        </div>
        <div className='option'>
          <input
            type='number'
            min='1'
            max='5'
            step='.5'
            value={gameSpeed}
            onChange={setGameSpeed}
          />
          Base Game Speed
        </div>
        <div className='option'>
          <input type='checkbox' checked={autopick} onChange={setAutopick} />
          Autopick (works like the checkbox in game)
        </div>
      </div>
      <div id='weapons' className='option-menu'>
        <div className='title'>Ultimate Weapons</div>
        <div className='option'>
          <input
            type='checkbox'
            checked={goldenTowerUnlocked}
            onChange={setGoldenTowerUnlocked}
          />
          Golden Tower Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={smartMissilesUnlocked}
            onChange={setSmartMissilesUnlocked}
          />
          Smart Missiles Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={deathWaveUnlocked}
            onChange={setDeathWaveUnlocked}
          />
          Death Wave Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={chainLightningUnlocked}
            onChange={setChainLightningUnlocked}
          />
          Chain Lightning Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={chronoFieldUnlocked}
            onChange={setChronoFieldUnlocked}
          />
          Chrono Field Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={innerLandMinesUnlocked}
            onChange={setInnerLandMinesUnlocked}
          />
          Inner Land Mines Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={poisonSwampUnlocked}
            onChange={setPoisonSwampUnlocked}
          />
          Poison Swamp Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={blackHoleUnlocked}
            onChange={setBlackHoleUnlocked}
          />
          Black Hole Unlocked
        </div>
        <div className='option'>
          <input
            type='checkbox'
            checked={spotlightUnlocked}
            onChange={setSpotlightUnlocked}
          />
          Spotlight Unlocked
        </div>
      </div>
      <div id='config' className='option-menu'>
        <div className='title'>Config</div>
        <div className='option'>
          <input
            type='number'
            min='1'
            value={simulationCount}
            onChange={setSimulationCount}
          />
          Simulation Count
        </div>
        <div className='option'>
          <input
            type='number'
            min='100'
            step='100'
            value={maxWave}
            onChange={setMaxWave}
          />
          Max Wave
        </div>
        <div className='option'>
          <button className='danger' type='button' onClick={resetAll}>
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
};

export default Options;
